import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogType?: string;
  ogImage?: string;
  schema?: object;
}

export const useSEO = ({
  title,
  description,
  keywords,
  canonical,
  ogType = 'website',
  ogImage = 'https://purecode.uk/og-image.jpg',
  schema
}: SEOProps) => {
  const location = useLocation();
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://purecode.uk';
  const fullUrl = canonical || `${siteUrl}${location.pathname}`;

  useEffect(() => {
    // Update title
    document.title = title;

    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };

    // Basic meta tags
    updateMetaTag('description', description);
    if (keywords) {
      updateMetaTag('keywords', keywords);
    }

    // Open Graph tags
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:url', fullUrl, true);
    updateMetaTag('og:type', ogType, true);
    updateMetaTag('og:image', ogImage, true);

    // Twitter tags
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);
    updateMetaTag('twitter:url', fullUrl);
    updateMetaTag('twitter:image', ogImage);

    // Canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', fullUrl);

    // Schema.org JSON-LD
    if (schema) {
      let schemaScript = document.querySelector('script[type="application/ld+json"]');
      if (!schemaScript) {
        schemaScript = document.createElement('script');
        schemaScript.setAttribute('type', 'application/ld+json');
        document.head.appendChild(schemaScript);
      }
      schemaScript.textContent = JSON.stringify(schema);
    }

    // Update last-modified
    const lastModified = new Date().toISOString().split('T')[0];
    updateMetaTag('last-modified', lastModified);
  }, [title, description, keywords, fullUrl, ogType, ogImage, schema]);
};

// Schema.org generators
export const generateOrganizationSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://purecode.uk';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'PURECODE Ltd',
    url: siteUrl,
    logo: `${siteUrl}/logo.png`,
    description: 'Premier IT consultancy specialising in AI-driven solutions for banking, fintech, and betting industries',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'The Genesis Centre, Birchwood',
      addressLocality: 'Warrington',
      addressRegion: 'England',
      postalCode: 'WA3 7BH',
      addressCountry: 'GB'
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: '53.390',
      longitude: '-2.597'
    },
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+44-1925-357468',
      contactType: 'customer service',
      email: 'hello@purecode.uk',
      availableLanguage: 'English'
    },
    sameAs: [
      'https://www.linkedin.com/company/purecodeltd'
    ],
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '09:00',
        closes: '17:00'
      }
    ]
  };
};

export const generateWebPageSchema = (pageName: string, description: string) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://purecode.uk';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: pageName,
    description: description,
    url: siteUrl,
    publisher: {
      '@type': 'Organization',
      name: 'PURECODE Ltd'
    }
  };
};

export const generateLocalBusinessSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://purecode.uk';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': `${siteUrl}/#localbusiness`,
    name: 'PURECODE Ltd',
    image: `${siteUrl}/logo.png`,
    url: siteUrl,
    telephone: '+44-1925-357468',
    email: 'hello@purecode.uk',
    priceRange: '££',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'The Genesis Centre, Birchwood',
      addressLocality: 'Warrington',
      addressRegion: 'England',
      postalCode: 'WA3 7BH',
      addressCountry: 'GB'
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: '53.390',
      longitude: '-2.597'
    },
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '09:00',
        closes: '17:00'
      }
    ],
    sameAs: [
      'https://www.linkedin.com/company/purecodeltd'
    ]
  };
};

export const generateServiceSchema = (serviceName: string, description: string) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://purecode.uk';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: serviceName,
    description: description,
    provider: {
      '@type': 'Organization',
      name: 'PURECODE Ltd',
      url: siteUrl
    },
    areaServed: {
      '@type': 'Country',
      name: 'United Kingdom'
    }
  };
};

export const generateBreadcrumbSchema = (items: Array<{ name: string; url: string }>) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://purecode.uk';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `${siteUrl}${item.url}`
    }))
  };
};

export const generateFAQSchema = (faqs: Array<{ question: string; answer: string }>) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer
      }
    }))
  };
};
